import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { api, runWithAuditReason } from '../../../api/client';
import {
  useCities, useCityStates, useSaveCity, useDeleteCity,
  errMessage, type CityRow, type StagedArea,
} from './city';
import { CityAreasPanel } from './CityAreasPanel';

interface Props { onHome?: () => void; }

/** State dropdown label — the State name (LongName), matching LIMS's CustomState
 *  lookup (which displays the "State" / LongName), and the grid's State column.
 *  Falls back to the short code only when there's no long name. */
function stateLabel(s: { shortName: string; longName: string | null }): string {
  return (s.longName ?? '').trim() || s.shortName;
}

/**
 * Settings ▸ System Management ▸ City — mirrors the legacy XAF `City_ListView`:
 * a master grid of cities (City name + State) with New / Edit / Delete. State is
 * a lookup to dbo.CustomState (shown as the short code, e.g. "TX").
 */
export function CityPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useCities();
  const { data: states = [] } = useCityStates();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveCity();
  const del = useDeleteCity();

  // Areas staged in memory for a brand-new (unsaved) city (committed on Save).
  const [staged, setStaged] = useState<StagedArea[]>([]);

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // Edit dialog: undefined = closed, null = New, row = Edit.
  const [editing, setEditing] = useState<CityRow | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [stateId, setStateId] = useState('');
  const [nameErr, setNameErr] = useState('');
  // Inline row-edit (the legacy first pencil): one row editable in place — City
  // becomes a text box and State a dropdown, with Save / Cancel in the icon cell.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftStateId, setDraftStateId] = useState('');
  const [savingInline, setSavingInline] = useState(false);
  const [maxed, setMaxed] = useState(false);  // open RIGHT-SIZED (not full-screen); user can maximize via the header button
  // When maximized, fill the CONTENT area (the grid card region — right of the nav,
  // below the header) rather than the whole viewport, matching the LIMS full-page
  // detail. Measure the card's live rect so it survives sidebar collapse / resize.
  const cardRef = useRef<HTMLDivElement>(null);
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maxed) { setMaxRect(null); return; }
    const measure = () => {
      const r = cardRef.current?.getBoundingClientRect();
      if (r) setMaxRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxed]);
  const maxStyle = maxed && maxRect
    ? { position: 'fixed' as const, top: maxRect.top, left: maxRect.left, width: maxRect.width,
        height: maxRect.height, maxWidth: 'none' as const, maxHeight: 'none' as const, margin: 0 }
    : undefined;

  const openNew = () => { setEditing(null); setName(''); setStateId(''); setNameErr(''); setStaged([]); setMaxed(false); };
  const openEdit = (r: CityRow) => { setEditing(r); setName(r.cityName ?? ''); setStateId(r.stateId ?? ''); setNameErr(''); setStaged([]); setMaxed(false); };

  const startInline = (r: CityRow) => { setInlineId(r.id); setDraftName(r.cityName ?? ''); setDraftStateId(r.stateId ?? ''); };
  const cancelInline = () => { setInlineId(null); setDraftName(''); setDraftStateId(''); };
  const saveInline = async (r: CityRow) => {
    if (!draftName.trim()) { notify.warning("'City' must not to be empty."); return; }
    // Reason-for-change gate (standing rule 11) — prompts only when the City form
    // has ReasonRequired ON; null = user cancelled the reason → abort the edit.
    const reason = await gateReason('City', 'edit');
    if (reason === null) return;
    setSavingInline(true);
    try {
      await runWithAuditReason(reason, () =>
        save.mutateAsync({ id: r.id, cityName: draftName.trim(), stateId: draftStateId || null }));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };
  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one city to edit.'); return; }
    const r = rows.find((x) => x.id === String([...selectedIds][0]));
    if (r) openEdit(r);
  };
  const close = () => { setEditing(undefined); setMaxed(false); setStaged([]); };
  const resetToNew = () => { setEditing(null); setName(''); setStateId(''); setNameErr(''); setStaged([]); };

  /** Save the City (create or update), commit any staged areas, then act per the
   *  button used: 'stay' (keep open — LIMS Save), 'close' (Save and Close),
   *  'new' (Save and New). */
  const doSave = async (after: 'stay' | 'close' | 'new') => {
    if (!name.trim()) { setNameErr("'City' must not to be empty."); return; }
    setNameErr('');
    const wasNew = !editing?.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT (existing
    // record); a brand-new City is a Create (audit-exempt, no reason).  null =
    // user cancelled the reason prompt → abort the save.
    let reason = '';
    if (!wasNew) {
      const r = await gateReason('City', 'edit');
      if (r === null) return;
      reason = r;
    }
    let newId: string | undefined;
    await runSave({
      save: async () => {
        await runWithAuditReason(reason, async () => {
          const res = await save.mutateAsync({ id: editing?.id, cityName: name.trim(), stateId: stateId || null });
          newId = editing?.id ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
          // Commit areas staged on a brand-new city (link existing / create new + link).
          if (wasNew && newId && staged.length) {
            for (const s of staged) {
              if (s.areaId) await api.post(`/api/v1/cities/${newId}/areas/link`, { areaId: s.areaId });
              else await api.post(`/api/v1/cities/${newId}/areas`, { areaName: s.name });
            }
          }
        });
      },
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        if (after === 'new') { resetToNew(); return; }
        // 'stay': a new city switches to edit mode (areas now committed); an existing edit stays open.
        if (wasNew && newId) {
          const st = states.find((s) => s.id === stateId);
          setEditing({ id: newId, cityName: name.trim(), stateId: stateId || null, state: st?.shortName ?? null });
          setStaged([]);
        }
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  // ── Inline State editor (New / Edit a State without leaving the City form) ──
  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one city to delete.'); return; }
    const ok = await confirm({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Delete ${selectedIds.size} cit${selectedIds.size === 1 ? 'y' : 'ies'}?`,
    });
    if (!ok) return;
    // Reason-for-change gate (standing rule 11) — prompts only when the City form
    // has ReasonRequired ON; null = cancelled → abort the delete.
    const reason = await gateReason('City', 'delete');
    if (reason === null) return;
    try {
      await runWithAuditReason(reason, async () => {
        for (const id of selectedIds) await del.mutateAsync(String(id));
      });
      setSelectedIds(new Set());
      await alert({ title: 'Success', message: 'Deleted.', tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.cityName, r.state].some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const textCmp = (get: (r: CityRow) => string | null) => (a: CityRow, b: CityRow) => {
    const av = (get(a) ?? '').trim(), bv = (get(b) ?? '').trim();
    if (av === '' && bv === '') return 0;
    if (av === '') return -1;
    if (bv === '') return 1;
    return av.localeCompare(bv, undefined, { sensitivity: 'base' });
  };

  const columns: AppColumn<CityRow>[] = [
    {
      // 1st pencil (legacy InlineEdit): toggle the row into in-place edit; in edit
      // mode it becomes Save (disk) + Cancel (undo).
      field: 'inlineedit', header: '', sortable: false, filterable: false, width: 46, align: 'center',
      render: (r) =>
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline}
              onClick={() => void saveInline(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline}
              onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit"
            onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-edit" /></button>
        ),
    },
    {
      // 2nd pencil (legacy open-detail): open the full Edit dialog for this row.
      // Hidden while this row is in inline edit (matches LIMS — the column goes
      // empty so only Save/Cancel show).
      field: 'editopen', header: '', sortable: false, filterable: false, width: 46, align: 'center',
      render: (r) =>
        inlineId === r.id ? null : (
          <button className="pts-icon-btn edit" title="Open full edit screen"
            onClick={(e) => { e.stopPropagation(); openEdit(r); }}><i className="ti ti-external-link" /></button>
        ),
    },
    {
      field: 'cityName', header: 'City', minWidth: 240, text: (r) => r.cityName ?? '', compare: textCmp((r) => r.cityName),
      render: (r) =>
        inlineId === r.id ? (
          <input className="pts-input" autoFocus value={draftName} disabled={savingInline}
            onClick={(e) => e.stopPropagation()} onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
        ) : (
          <span>{r.cityName ?? ''}</span>
        ),
    },
    {
      field: 'state', header: 'State', width: 180, sortable: false, text: (r) => r.state ?? '',
      render: (r) =>
        inlineId === r.id ? (
          <span className="pts-terms-edit" onClick={(e) => e.stopPropagation()}>
            <select className="pts-select" value={draftStateId} disabled={savingInline}
              onChange={(e) => setDraftStateId(e.target.value)}>
              <option value="">N/A</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>{stateLabel(s)}</option>
              ))}
            </select>
          </span>
        ) : (
          <span>{r.state ?? ''}</span>
        ),
    },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" />
          <span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>City</span>
        </div>
        {/* BO UI Standard v2: FormTitle (nav icon + title + load-time + audit chip)
            ON the grey page, then the "N items" subtitle — never inside the card. */}
        <FormTitle pageId="sm-city" loadMs={loadMs ?? null} loading={isFetching} auditFormName="City" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>

      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row below the title (BO UI Standard §6).
            Canonical order via the shared <AppToolbar>; Export rides the spacer far right. */}
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="City" headers={['City', 'State']}
                rows={visible.map((r) => [r.cityName ?? '', r.state ?? ''])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card" ref={cardRef}>
          <AppDataGrid<CityRow>
            rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            selectedRowId={inlineId ?? editing?.id ?? null}
            initialSort={{ key: 'cityName', dir: 'asc' }}
            loading={isFetching} emptyText="No cities found."
          />
        </div>
      </div>

      {editing !== undefined && (
        <div className="acc-overlay" onMouseDown={close}>
          <div className={`acc-dialog acc-dialog-md${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{editing ? `City - ${editing.cityName ?? ''}` : 'City'}</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`}
                  title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={close} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">City<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={name}
                    onChange={(e) => { setName(e.target.value); if (nameErr) setNameErr(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void doSave('stay'); }} />
                  {nameErr && <div className="acc-err">{nameErr}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">State</label>
                <div className="acc-fh-control">
                  {/* State lookup: dropdown + erase (clear) icon — LIMS CustomState lookup editor. */}
                  <div className="acc-lookup">
                    <select className="acc-input" value={stateId} onChange={(e) => setStateId(e.target.value)}>
                      <option value="">N/A</option>
                      {states.map((s) => (
                        <option key={s.id} value={s.id}>{stateLabel(s)}</option>
                      ))}
                    </select>
                    <button className="acc-lookup-btn" title="Erase" disabled={!stateId} onClick={() => setStateId('')}><i className="ti ti-eraser" /></button>
                  </div>
                </div>
              </div>
              {/* Area child collection — mirrors City_DetailView ▸ Area. On a new city
                  areas stage in memory (staged/onStaged) and commit on Save; on an
                  existing city they commit immediately. */}
              <CityAreasPanel cityId={editing?.id} staged={staged} onStaged={setStaged} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void doSave('stay')} disabled={save.isPending}><span className="tb-i">💾</span> Save</button>
              <button className="ca-btn" onClick={() => void doSave('close')} disabled={save.isPending}><span className="tb-i">💾</span> Save and Close</button>
              <button className="ca-btn" onClick={() => void doSave('new')} disabled={save.isPending}><span className="tb-i">💾</span> Save and New</button>
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
